import React, { useMemo, forwardRef, type ReactNode } from 'react';
import { Box } from '#components/box/box.tsx';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import { UtilityText } from '#components/typography/utility-text/utility-text.tsx';
import type {
  GDSCustomizableComponent,
  GDSHTMLAttributes,
} from '#src/types.ts';
import type { GDSHorizontalAlignment } from '#components/stack-layout/stack-layout.types.ts';
import { styled } from '../../theme';
import { GenesisCoreInspector } from '../../test-utils/genesis-core-inspector';

export interface GDSProgressBarProps
  extends GDSCustomizableComponent,
    GDSHTMLAttributes {
  accessibilityLabel?: string;
  fill?: 'neutral' | 'brand';
  fillProgressValue?: number;
  height?: 'small' | 'medium' | 'large';
  label?: string;
  labelPosition?: 'topLeading' | 'topCenter' | 'bottomLeading' | 'bottomCenter';
  leadingValue?: string;
  maxProgressValue?: number;
  minProgressValue?: number;
  stopIndicator?: boolean;
  trailingValue?: string;
  valuePosition?: 'top' | 'bottom';
  width?: number | string;
}

const DISPLAY_NAME = 'ProgressBar';

const BaseProgressBar = styled(Box, {
  // Override global box-sizing: border-box for this one element
  '&&': { boxSizing: 'content-box' },
  position: 'relative',
  borderStyle: 'solid',
  borderWidth: '$thin',
  borderColor: '$dataVizInterfaceTrackBorder',
  backgroundColor: '$dataVizInterfaceTrackBackground',
  borderRadius: '$progressBar',
  height: 'auto',
  variants: {
    height: {
      small: { height: '$quarter' },
      medium: { height: '$half' },
      large: { height: '$one' },
    },
  },
});

const Track = styled(Box, {
  height: '100%',
  position: 'absolute',
  borderRadius: '$progressBar',
  variants: {
    fill: {
      neutral: { backgroundColor: '$dataVizSingleColorNeutral' },
      brand: { backgroundColor: '$dataVizSingleColorBrand' },
    },
  },
});

const StopIndicator = styled(Box, {
  position: 'absolute',
  '&:dir(ltr)': { right: 0 },
  '&:dir(rtl)': { left: 0 },
  height: '100%',
  borderRadius: '$progressBar',
  variants: {
    fill: {
      neutral: { backgroundColor: '$dataVizSingleColorNeutral' },
      brand: { backgroundColor: '$dataVizSingleColorBrand' },
    },
    width: {
      small: { width: '$quarter' },
      medium: { width: '$half' },
      large: { width: '$one' },
    },
  },
});

const Label = ({
  children,
  alignment,
}: {
  children: ReactNode;
  alignment: GDSProgressBarProps['labelPosition'];
}) => (
  <UtilityText
    as="label"
    size="sm"
    emphasis="emphasized"
    className="GDS-progress-bar-label"
    css={{ alignSelf: alignment?.includes('Center') ? 'center' : 'flex-start' }}
  >
    {children}
  </UtilityText>
);

const getHorizontalAlignment = (
  leadingValue?: string,
  trailingValue?: string,
): GDSHorizontalAlignment => {
  if (leadingValue && trailingValue) return 'spaceBetween';
  if (leadingValue && !trailingValue) return 'start';
  return 'end';
};

const Values = ({
  leadingValue,
  trailingValue,
}: Pick<GDSProgressBarProps, 'leadingValue' | 'trailingValue'>) => {
  // No component if values are not provided
  if (!leadingValue && !trailingValue) {
    return null;
  }

  return (
    <StackLayout
      orientation="horizontal"
      spacing="$one"
      horizontalAlignment={getHorizontalAlignment(leadingValue, trailingValue)}
      className="GDS-progress-bar-values"
    >
      <UtilityText size="sm" className="GDS-progress-bar-leading-value">
        {leadingValue}
      </UtilityText>
      <UtilityText
        size="sm"
        css={{ textAlign: 'end' }}
        className="GDS-progress-bar-trailing-value"
      >
        {trailingValue}
      </UtilityText>
    </StackLayout>
  );
};

export const ProgressBar = forwardRef<HTMLDivElement, GDSProgressBarProps>(
  (
    {
      'data-testid': testId = `GDS-${DISPLAY_NAME}`,
      accessibilityLabel,
      className,
      css,
      fill = 'brand',
      fillProgressValue = 0,
      height = 'medium',
      label,
      labelPosition = 'topCenter',
      leadingValue,
      maxProgressValue = 100,
      minProgressValue = 0,
      stopIndicator = false,
      trailingValue,
      valuePosition = 'top',
      width,
      ...props
    },
    ref,
  ) => {
    // Calculate Track width
    const trackWidth = useMemo(() => {
      // Render empty progress bar if minProgressValue is greater than or equal to maxProgressValue
      if (minProgressValue >= maxProgressValue) {
        return '0%';
      }

      // Calculate percentage
      const percentage =
        ((fillProgressValue - minProgressValue) /
          (maxProgressValue - minProgressValue)) *
        100;

      // Ensure percentage is between 0 and 100
      return `${Math.max(0, Math.min(100, percentage))}%`;
    }, [fillProgressValue, minProgressValue, maxProgressValue]);

    return (
      <GenesisCoreInspector displayName={DISPLAY_NAME}>
        <StackLayout
          className={['GDS-progress-bar', className].join(' ')}
          horizontalAlignment="stretch"
          spacing="$quarter"
          css={{
            width: width || '100%',
            ...css,
          }}
        >
          {label && labelPosition.includes('top') && (
            <Label alignment={labelPosition}>{label}</Label>
          )}
          {valuePosition === 'top' && (
            <Values leadingValue={leadingValue} trailingValue={trailingValue} />
          )}
          <BaseProgressBar
            aria-label={accessibilityLabel}
            aria-valuemax={maxProgressValue}
            aria-valuemin={minProgressValue}
            aria-valuenow={trackWidth.replace('%', '')}
            className="GDS-progress-bar-background"
            data-testid={testId}
            height={height}
            ref={ref}
            role="progressbar"
            css={{
              width: '100%',
            }}
            {...props}
          >
            <Track
              className="GDS-progress-bar-track"
              fill={fill}
              css={{ width: trackWidth }}
            />
            {stopIndicator && (
              <StopIndicator
                className="GDS-progress-bar-stop-indicator"
                fill={fill}
                width={height}
              />
            )}
          </BaseProgressBar>
          {valuePosition === 'bottom' && (
            <Values leadingValue={leadingValue} trailingValue={trailingValue} />
          )}
          {label && labelPosition.includes('bottom') && (
            <Label alignment={labelPosition}>{label}</Label>
          )}
        </StackLayout>
      </GenesisCoreInspector>
    );
  },
);

ProgressBar.displayName = DISPLAY_NAME;
